import { useRequest } from "ahooks";
import { FC, useEffect, useRef } from "react";
import { message, QRCode, Spin } from "antd";
import classNames from "classnames";

import styles from "../../index.less";

import { ReactComponent as SpinIcon } from "../../../../../../assets/svg/spin.svg";
import { ReactComponent as ReloadIcon } from "../../../../../../assets/svg/reload.svg";

import { ResponseCode } from "../../../../../../constants";

import {
  DisplayPayStatusEnum,
  getPayCallbackService,
  OrderInfo,
  PayType,
  requestPayService,
} from "../../../../../../service";
import { AliPayId } from "../../../../../../constants";

interface PayQRCodeProps {
  status: DisplayPayStatusEnum;
  updateStatus: React.Dispatch<React.SetStateAction<DisplayPayStatusEnum>>;
  orderInfo?: OrderInfo;
  payType: PayType;
  onSuccess: (payId: string) => void;
  setQRCodeExpiredTime: (time: number) => void;
}

const PayQRCode: FC<PayQRCodeProps> = ({
  orderInfo,
  payType,
  onSuccess,
  status,
  updateStatus,
  setQRCodeExpiredTime,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (status === DisplayPayStatusEnum.QR_CODE_EXPIRED) {
      cancel();
    }
  }, [status]);

  const { cancel, run: fetchStatus } = useRequest(
    async (payId: string) => {
      const rsp = await getPayCallbackService({
        payId: payId || "",
      });

      if (rsp.code !== ResponseCode.Success) {
        cancel();
        message.error(rsp.msg);
        return;
      }

      if (rsp?.data === DisplayPayStatusEnum.DONE) {
        setTimeout(() => {
          onSuccess(payId);
        }, 1000);
        cancel();
        return;
      }

      updateStatus(rsp?.data);
    },
    {
      pollingInterval: 1000,
      manual: true,
    }
  );

  const { data: requestWechatPayInfo, runAsync: manualRequestWechatPay } =
    useRequest(
      async () => {
        const rsp = await requestPayService({
          orderId: orderInfo?.orderId || "",
          payType: PayType.WECHAT,
        });

        if (rsp.code !== ResponseCode.Success) {
          message.error(rsp.msg);
          return;
        }

        updateStatus(DisplayPayStatusEnum.PAYING);

        setQRCodeExpiredTime(rsp.data.expireTime * 1000);
        // setQRCodeExpiredTime(Date.now() + 10 * 1000);
        fetchStatus(rsp.data.payId);
        return rsp.data;
      },
      {
        ready: !!orderInfo && payType === PayType.WECHAT,
        debounceWait: 1000,
      }
    );

  const refreshQRCode = async () => {
    cancel();
    setQRCodeExpiredTime(0);
    updateStatus(() => DisplayPayStatusEnum.QR_CODE_LOADING);

    if (payType === PayType.WECHAT) {
      await manualRequestWechatPay();
    }

    if (payType === PayType.ALIPAY) {
      updateStatus(DisplayPayStatusEnum.PAYING);
      iframeRef.current!.src = iframeRef.current!.src;
    }
  };

  useEffect(() => {
    if (payType === PayType.ALIPAY) {
      updateStatus(DisplayPayStatusEnum.PAYING);

      const cb = (e: MessageEvent) => {
        if (typeof e.data === "object" && e.data.type === AliPayId) {
          setQRCodeExpiredTime(e.data.expireTime * 1000);
          // setQRCodeExpiredTime(Date.now() + 10 * 1000);
          fetchStatus(e.data.payId);
        }
      };

      window.addEventListener("message", cb);

      return () => {
        cancel();
        window.removeEventListener("message", cb);
        updateStatus(DisplayPayStatusEnum.WAIT_FOR_AGREE_TERMS);
      };
    }
  }, [payType]);

  return (
    <>
      {/* 支付二维码加载 */}
      {status === DisplayPayStatusEnum.QR_CODE_LOADING && (
        <div
          className={classNames(
            styles["qrcode-box-common"],
            styles["qrcode-box-loading"]
          )}
        >
          <div className={styles["text"]}>二维码获取中</div>
          <Spin indicator={<SpinIcon className="animation-spin" />} />
        </div>
      )}

      {/* 二维码过期 */}
      {status === DisplayPayStatusEnum.QR_CODE_EXPIRED && (
        <div
          className={classNames(
            styles["qrcode-box-common"],
            styles["qrcode-box-expired"]
          )}
          onClick={async () => {
            await refreshQRCode();
          }}
        >
          <div className={styles["text"]}>
            二维码已过期
            <br />
            请点击刷新
          </div>

          <ReloadIcon />
        </div>
      )}

      {/* 支付取消 */}
      {status === DisplayPayStatusEnum.CANCEL && (
        <div
          className={classNames(
            styles["qrcode-box-common"],
            styles["qrcode-box-cancel"]
          )}
          onClick={async () => {
            await refreshQRCode();
          }}
        >
          <div className={styles["text"]}>
            支付已取消
            <br />
            请重新获取二维码
          </div>

          <ReloadIcon />
        </div>
      )}

      <div
        className={classNames(styles["qrcode-box-common"], {
          [styles.hidden]: status !== DisplayPayStatusEnum.PAYING,
        })}
      >
        <iframe
          ref={iframeRef}
          src={
            payType === PayType.ALIPAY
              ? `${location.protocol}//${location.host}/alipay?orderId=${
                  orderInfo?.orderId
                }&qrcodeSize=${126}`
              : ""
          }
          className={classNames(styles.qrcode, {
            [styles.hidden]: payType !== PayType.ALIPAY,
          })}
          frameBorder={0}
          scrolling="no"
        />

        <QRCode
          size={126}
          bordered={false}
          value={requestWechatPayInfo?.codeUrl || "loading"}
          className={classNames(styles.qrcode, {
            [styles.hidden]: payType !== PayType.WECHAT,
          })}
        />
      </div>
    </>
  );
};

export default PayQRCode;
